import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  filterPartInlineStyle,
  resolveCatalogueFiltersPartStyles,
  type CatalogueFiltersParts,
} from "@/lib/cms-puck/blocks/catalogue-filters";
import { cn } from "@/lib/utils";
import { Filter, Search } from "lucide-react";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

/**
 * Classes ALIGNÉES sur `FilterToolbar` du front public
 * (`inventozen-frontpublic/src/components/FilterToolbar.tsx`).
 */
const filterSelectTriggerClass =
  "w-fit border-none rounded-lg bg-transparent shadow-none text-foreground hover:cms-page-hover focus:ring-0";

const filterSearchWrapClass =
  "relative flex items-center gap-2 border cms-page-border rounded-lg pl-2 bg-transparent text-foreground";

const filterPanelWrapClass =
  "flex items-center border cms-page-border rounded-lg gap-2 px-3 text-center text-foreground";

/**
 * Aperçu statique de la barre de filtres catalogue — même structure visuelle
 * que `PuckCatalogFilters` / `FilterToolbar` sur le site public.
 */
export function CatalogueDecoupagesFiltersPreview({
  target = "ARTWORKS",
  parts,
}: {
  target?: "ARTWORKS" | "MULTIPRINTS";
  parts?: CatalogueFiltersParts;
}) {
  const cmsNew = useCmsNew();
  const theme = usePuckTheme();
  const partStyles = resolveCatalogueFiltersPartStyles(
    theme?.colors ?? [],
    parts,
  );
  const selectsStyle = filterPartInlineStyle(partStyles.selects);
  const searchStyle = filterPartInlineStyle(partStyles.search);
  const moreStyle = filterPartInlineStyle(partStyles.moreButton);

  const typeLabel =
    target === "MULTIPRINTS"
      ? cmsNew(
          "cms.new.preview.decoupagesFilters.typeMultiple",
          "Filtrer par type",
        )
      : cmsNew(
          "cms.new.preview.decoupagesFilters.typeArtwork",
          "Filtrer par type",
        );

  return (
    <div className="pointer-events-none w-full select-none" aria-hidden>
      <div className="hidden md:block">
        <div className="flex w-full items-center justify-between">
          <div
            className={cn(
              "flex items-center gap-1 rounded-lg cms-page-surface p-0.5",
              partStyles.selects?.color && "[&_*]:!text-[inherit]",
            )}
            style={selectsStyle}
          >
            <Select disabled value="">
              <SelectTrigger className={filterSelectTriggerClass}>
                <SelectValue
                  placeholder={cmsNew(
                    "cms.new.preview.decoupagesFilters.date",
                    "Filtrer par date",
                  )}
                />
              </SelectTrigger>
              <SelectContent />
            </Select>
            <Select disabled value="">
              <SelectTrigger className={filterSelectTriggerClass}>
                <SelectValue placeholder={typeLabel} />
              </SelectTrigger>
              <SelectContent />
            </Select>
            {target === "ARTWORKS" ? (
              <Select disabled value="">
                <SelectTrigger className={filterSelectTriggerClass}>
                  <SelectValue
                    placeholder={cmsNew(
                      "cms.new.preview.decoupagesFilters.themes",
                      "Thèmes",
                    )}
                  />
                </SelectTrigger>
                <SelectContent />
              </Select>
            ) : null}
            <Select disabled value="">
              <SelectTrigger className={filterSelectTriggerClass}>
                <SelectValue
                  placeholder={cmsNew(
                    "cms.new.preview.decoupagesFilters.keywords",
                    "Mots-clés",
                  )}
                />
              </SelectTrigger>
              <SelectContent />
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <div
              className={cn(
                filterSearchWrapClass,
                partStyles.search?.color && "[&_*]:!text-[inherit]",
              )}
              style={searchStyle}
            >
              <Search size={24} className="shrink-0 text-foreground" />
              <Input
                readOnly
                tabIndex={-1}
                placeholder={cmsNew(
                  "cms.new.auto.placeholder.rechercher",
                  "Rechercher",
                )}
                className="min-w-64 border-none bg-transparent shadow-none"
              />
            </div>
            <div
              className={cn(
                filterPanelWrapClass,
                partStyles.moreButton?.color && "[&_*]:!text-[inherit]",
              )}
              style={moreStyle}
            >
              <Filter size={18} />
              <Button
                type="button"
                variant="ghost"
                className="border-none px-0"
                tabIndex={-1}
              >
                {cmsNew(
                  "cms.new.preview.decoupagesFilters.moreFilters",
                  "Plus de filtres",
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 md:hidden">
        <div className={`${filterSearchWrapClass} w-full`} style={searchStyle}>
          <Search size={20} className="shrink-0 text-foreground" />
          <Input
            readOnly
            tabIndex={-1}
            placeholder={cmsNew(
              "cms.new.auto.placeholder.rechercher",
              "Rechercher",
            )}
            className="border-none bg-transparent shadow-none"
          />
        </div>
        <div className={filterPanelWrapClass} style={moreStyle}>
          <Filter size={16} />
        </div>
      </div>
    </div>
  );
}
